import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Turnstile for the hosted report page.
 *
 * The embedded SDK has its own implementation; this is the same protocol
 * for the React page, which cannot import from `apps/widget` (different
 * bundle, different build).
 *
 * Why it is not optional: in production `assertConfigured` requires
 * Turnstile to be configured, and the submission endpoint then rejects any
 * request without a token. A page that never mints one gets a 403 on
 * every submission and shows every reporter "we could not verify that you
 * are human" — which is exactly what this page did before, making the
 * whole feature non-functional in production while working fine locally
 * (where the captcha is intentionally disabled). Local-only testing could
 * never have caught it.
 *
 * The action MUST match `WIDGET_TURNSTILE_ACTION` in the api, which
 * verifies it along with the hostname.
 */

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const ACTION = 'toado_widget_submit';
const TOKEN_TIMEOUT_MS = 20_000;

interface TurnstileGlobal {
  render(container: HTMLElement, options: Record<string, unknown>): string;
  reset(widgetId?: string): void;
  remove(widgetId?: string): void;
}

declare global {
  interface Window { turnstile?: TurnstileGlobal }
}

let scriptPromise: Promise<TurnstileGlobal | null> | null = null;

function loadScript(): Promise<TurnstileGlobal | null> {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve) => {
    if (window.turnstile) { resolve(window.turnstile); return; }
    const el = document.createElement('script');
    el.src = SCRIPT_URL;
    el.async = true;
    el.defer = true;
    const timer = window.setTimeout(() => resolve(null), 10_000);
    el.onload = () => { window.clearTimeout(timer); resolve(window.turnstile ?? null); };
    el.onerror = () => { window.clearTimeout(timer); resolve(null); };
    document.head.appendChild(el);
  });
  return scriptPromise;
}

export interface TurnstileHandle {
  /**
   * Attach to the element the challenge should render into.
   *
   * Nullable because that is what `useRef<HTMLDivElement>(null)` has always
   * produced: the ref holds null until React attaches the node. React 19's
   * types say so (`RefObject<T | null>`) where 18's did not, so this is the
   * declaration catching up to the runtime rather than a behaviour change.
   */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Resolves with a token, or null when Turnstile is unavailable. */
  getToken: () => Promise<string | null>;
  /** True once an interactive challenge is on screen, so layout can adapt. */
  interactive: boolean;
}

/**
 * `siteKey` is null when the deployment has no captcha configured (local
 * development). The hook then no-ops and `getToken` resolves null, which
 * the server accepts in that same configuration.
 */
export function useTurnstile(siteKey: string | null): TurnstileHandle {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | undefined>(undefined);
  const resolveRef = useRef<((token: string | null) => void) | null>(null);
  const cachedRef = useRef<string | null>(null);
  const [interactive, setInteractive] = useState(false);

  useEffect(() => {
    if (!siteKey) return undefined;
    let cancelled = false;

    void loadScript().then((turnstile) => {
      if (cancelled || !turnstile || !containerRef.current) return;
      try {
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action: ACTION,
          size: 'flexible',
          appearance: 'interaction-only',
          callback: (token: string) => {
            // Hand a waiting caller the token directly and do NOT cache
            // it. Turnstile tokens are single use, so a cached one would
            // be replayed on the next attempt and rejected.
            const waiting = resolveRef.current;
            if (waiting) {
              resolveRef.current = null;
              waiting(token);
              return;
            }
            cachedRef.current = token;
          },
          'error-callback': () => {
            resolveRef.current?.(null);
            resolveRef.current = null;
          },
          'expired-callback': () => { cachedRef.current = null; },
          'before-interactive-callback': () => setInteractive(true),
        });
      } catch {
        // Rendering failed; getToken resolves null and the server decides.
      }
    });

    return () => {
      cancelled = true;
      // Release anyone awaiting a token so a pending promise cannot
      // outlive the page.
      resolveRef.current?.(null);
      resolveRef.current = null;
      void loadScript().then((turnstile) => {
        try { turnstile?.remove(widgetIdRef.current); } catch { /* already gone */ }
      });
    };
  }, [siteKey]);

  const getToken = useCallback(async (): Promise<string | null> => {
    if (!siteKey) return null;
    const turnstile = await loadScript();
    if (!turnstile) return null;

    if (cachedRef.current) {
      const token = cachedRef.current;
      cachedRef.current = null;
      turnstile.reset(widgetIdRef.current);
      return token;
    }

    return new Promise<string | null>((resolve) => {
      // Single settle path: a challenge that neither succeeds nor errors
      // must not leave submit stuck on "Sending…" forever.
      let settled = false;
      const settle = (token: string | null): void => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        resolveRef.current = null;
        resolve(token);
      };
      const timer = window.setTimeout(() => settle(null), TOKEN_TIMEOUT_MS);
      resolveRef.current = settle;
      try {
        turnstile.reset(widgetIdRef.current);
      } catch {
        settle(null);
      }
    });
  }, [siteKey]);

  return { containerRef, getToken, interactive };
}
